<script lang="ts" setup>
import { onMounted, ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePayStore } from '@/modules/pay/store/payStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useRouter } from 'vue-router'
import { useApiStore } from '@/modules/api/store/apiStore'
// Стор аутентификации — используется для проверки isAuth перед оплатой
import { useAuthStore } from '@/modules/auth/stores/authStore'
import TariffCard from '@/components/tariff-card/TariffCard.vue'
import type { ITariff } from '@/modules/api/types/ITariff'
import type { ITariffCardView } from '@/types/ITariffCardView'
import { ERouteNames } from '@/router/ERouteNames'
import { PaymentMethod } from '@vpn-tg/vue-client-grpc/proto/enums/paymentMethod'
import { useUserStore } from '@/modules/user/stores/userStore'

const { t } = useI18n()
const payStore = usePayStore()
const feedbackStore = useFeedbackStore()
const apiStore = useApiStore()
const authStore = useAuthStore()
const userStore = useUserStore()

const router = useRouter()

const props = defineProps({
  buttonText: {
    type: String,
    default: '',
  },
})

const errors = ref({
  promo: '',
})

const selectedTariff = ref('')

// === Маппинг тарифов из бэка в карточки ===
function mapTariffToView(tariff: ITariff): ITariffCardView {
  // Указываем тип явно!
  let tag: string | undefined = undefined
  let tagType: 'popular' | 'profitable' | undefined = undefined

  if (tariff.isPopular) {
    tag = t('tariffs.popular')
    tagType = 'popular'
  }
  if (tariff.periodDays === 365) {
    tag = t('tariffs.profitable')
    tagType = 'profitable'
  }
  if (tariff.isPromo) {
    tag = t('tariffs.promoPeriod')
  }

  return {
    id: tariff.id,
    value: tariff.id, // <-- id из бэка, string
    duration: `${tariff.periodName} - ${tariff.price}`,
    tag,
    tagType,
    economy: tariff.economy ? Math.round(tariff.economy) : undefined,
    isPromo: tariff.isPromo,
  }
}

// computed для массива карточек
const tariffs = computed(() => {
  if (!Array.isArray(apiStore.tariffs)) return []
  
  let list = apiStore.tariffs
  // Если пользователь вошел и уже использовал промо-тариф, убираем промо-тарифы из списка
  if (authStore.isAuth && userStore.user?.hasUsedPromo) {
    list = list.filter((t) => !t.isPromo)
  }
  
  return list.map(mapTariffToView)
})

// Выбор тарифа
watch(
  tariffs,
  (val) => {
    if (!val.length) return

    // Находим "Популярный"
    const popular = val.find((t) => t.tagType === 'popular')
    selectedTariff.value = popular?.id ?? val[0].id
  },
  { immediate: true },
)

function handleSelectTariff(val: string) {
  selectedTariff.value = val
}

const isSelectedTariffPromo = computed(() => {
  if (!Array.isArray(apiStore.tariffs)) return false
  const selected = apiStore.tariffs.find((t) => t.id === selectedTariff.value)
  return selected?.isPromo ?? false
})

const selectedTariffPrice = computed(() => {
  if (!Array.isArray(apiStore.tariffs)) return 0
  const selected = apiStore.tariffs.find((t) => t.id === selectedTariff.value)
  return selected?.price ?? 0
})

const verificationForm = async () => {
  if (!selectedTariff.value) {
    feedbackStore.showToast({
      type: 'error',
      message: t('tariffs.selectTariffError'),
    })
    return
  }

  // Гость — сохраняем выбранный тариф в localStorage и отправляем на /auth.
  // После успешного логина payStore.tryStartPendingPayment() прочитает ключ и запустит оплату.
  if (!authStore.isAuth) {
    localStorage.setItem('vpn_pending_tariff_id', selectedTariff.value)
    await router.push({ name: ERouteNames.AUTH })
    return
  }

  payStore.selectedTariff = selectedTariff.value

  if (isSelectedTariffPromo.value && !userStore.user?.telegramID) {
    feedbackStore.showToast({
      type: 'error',
      message: t('tariffs.telegramBindRequired'),
      time: 15000,
    })
    await router.push({ name: ERouteNames.PROFILE })
    return
  }

  const createPaymentPayload = {
    tariffID: payStore.selectedTariff,
    paymentMethod: PaymentMethod.YooKassa,
  }

  const result = await payStore.createPayment(createPaymentPayload)
  if (result.ok) {
    await router.push({ name: ERouteNames.PAYMENT })
  } else {
    feedbackStore.showToast({
      type: 'error',
      message: result.error || '',
    })
  }
}

onMounted(async () => {
  if (!apiStore.tariffs.length) {
    await apiStore.getTariffs()
  }
})
</script>

<template>
  <div class="tariffs-container d-flex flex-column ga-4">
    <div class="tariff__header d-flex justify-center align-center cursor-default">
      <div class="tariff__title">{{ t('tariffs.title') }}</div>
    </div>

    <div class="tariff__body d-flex flex-column ga-2">
      <TariffCard
        v-for="tariff in tariffs"
        :key="tariff.id"
        :tariff="tariff"
        :checked="selectedTariff === tariff.value"
        @select="handleSelectTariff"
      />
    </div>

    <div v-if="errors.promo" class="promo__input-error-interface">
      <div class="text-error text-micro">
        <span>{{ errors.promo }}</span>
      </div>
    </div>

    <div class="tariff__action">
      <v-btn
        variant="flat"
        color="primary"
        rounded="lg"
        block
        type="submit"
        :disabled="isSelectedTariffPromo && userStore.user?.hasUsedPromo"
        @click="verificationForm"
      >
        {{ isSelectedTariffPromo ? t('tariffs.tryFree', { price: selectedTariffPrice }) : (props.buttonText || t('tariffs.subscribe')) }}
      </v-btn>
      <div v-if="isSelectedTariffPromo && userStore.user?.hasUsedPromo" class="text-error text-center mt-2 text-caption">
        {{ t('tariffs.promoUsed') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.tariff__header {
  height: 20px;
}

.tariff__title {
  font-size: 18px;
  font-weight: 500;
  text-align: center;
}

.tariff__body {
  gap: 8px;
}
</style>

